/** Finite wind-oriented sand provinces on native warm, dry, low ground.
 * The shared heightfield owns the dunes; neither time nor mesh LOD enters here. */
import {hashSeed,rngFromString} from './rng.js';
import * as pmath from './pmath.js';
import type {PlanetDef} from './universe.js';

type V3=readonly[number,number,number];
export interface DuneRidge {
  readonly offsetM:number;
  readonly spacingM:number;
  readonly heightM:number;
  readonly meanderM:number;
  readonly centerUM:number;
  readonly halfLengthM:number;
  readonly seed:number;
}
export interface DuneDef {
  readonly id:string;
  readonly seed:number;
  readonly center:V3;
  /** Along the connected crests; across points toward the short lee face. */
  readonly along:V3;
  readonly across:V3;
  readonly halfLengthM:number;
  readonly halfWidthM:number;
  /** Coarse native substrate, 5×5, across first. Includes native lake basins,
   * never dunes, traced rivers, coast overrides or settlement edits. */
  readonly foundation:readonly number[];
  readonly ridges:readonly DuneRidge[];
  readonly corridorM:number;
}
export interface DuneNativeSample {heightM:number;temp:number;moist:number;mountain:number;plateau:number}
export interface DuneSample {heightM:number;sand:number;weight:number}
export const duneSample=():DuneSample=>({heightM:0,sand:0,weight:0});
const smooth=(v:number):number=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t)};
const norm=(x:number,y:number,z:number):V3=>{const r=Math.sqrt(x*x+y*y+z*z);return[x/r,y/r,z/r]};
const dot=(a:V3,b:V3):number=>a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const cross=(a:V3,b:V3):V3=>[a[1]*b[2]-a[2]*b[1],a[2]*b[0]-a[0]*b[2],a[0]*b[1]-a[1]*b[0]];
export function duneDirection(f:Pick<DuneDef,'center'|'along'|'across'>,radius:number,u:number,v:number):V3 {
  return norm(f.center[0]*radius+f.along[0]*u+f.across[0]*v,f.center[1]*radius+f.along[1]*u+f.across[1]*v,f.center[2]*radius+f.along[2]*u+f.across[2]*v);
}
/** Warm/dry living patches, not a synonym for the desert planet archetype. */
export function duneClimate(temp:number,moist:number):number {
  return smooth((temp-.10)/.14)*smooth((-.18-moist)/.20);
}
function corner(k:number,seed:number):number{
  let h=seed^Math.imul(k,374761393);h=Math.imul(h^(h>>>13),1274126177);h^=h>>>16;return(h>>>0)/2147483648-1;
}
function field(x:number,seed:number):number{
  const i=Math.floor(x),t=smooth(x-i);return corner(i,seed)*(1-t)+corner(i+1,seed)*t;
}
function fieldSlope(x:number,seed:number):number{
  const i=Math.floor(x),t=x-i;return(corner(i+1,seed)-corner(i,seed))*6*t*(1-t);
}
function commonBend(f:DuneDef,u:number):number{
  const s=u/f.halfLengthM,sign=corner(0,f.seed^0x41524353)<0?-1:1;
  return f.halfWidthM*(.24*(s*s-.3)*sign+.18*field(u/1800+.37,f.seed^0x57494e44));
}
function commonBendSlope(f:DuneDef,u:number):number{
  const sign=corner(0,f.seed^0x41524353)<0?-1:1;
  return f.halfWidthM*(.48*u/(f.halfLengthM*f.halfLengthM)*sign+.18*fieldSlope(u/1800+.37,f.seed^0x57494e44)/1800);
}
/** Almost planar ramps with short smooth toe/crest joins. */
function ramp(v:number):number{
  const t=Math.max(0,Math.min(1,v)),e=.045;
  if(t<e)return t*t/(2*e*(1-e));if(t>1-e)return 1-(1-t)*(1-t)/(2*e*(1-e));return(t-e*.5)/(1-e);
}
/** Relative sand relief only, useful for numerical profile inspection. */
export function duneRelief(f:DuneDef,u:number,v:number):number{
  let height=0;const sharedBend=commonBend(f,u),sharedSlope=commonBendSlope(f,u);
  for(const ridge of f.ridges){
    const extent=1-Math.abs(u-ridge.centerUM)/ridge.halfLengthM;if(extent<=0)continue;
    const windward=ridge.spacingM*.68,rough=v-ridge.offsetM-sharedBend;
    const slopeBound=Math.abs(sharedSlope)+ridge.meanderM*(2.25/1100+.75/2300);
    const maxLee=ridge.heightM/.55*(1+slopeBound*slopeBound*.5);
    // Reject irrelevant crests before evaluating their hash fields. The bound
    // includes every possible bend, peak height and curvature-stretched lee.
    if(rough<=-windward-ridge.meanderM||rough>=maxLee+ridge.meanderM)continue;
    const taper=smooth(extent/.42);
    const bend=sharedBend+ridge.meanderM*(.75*field(u/1100,ridge.seed)+.25*field(u/2300,ridge.seed^0x42454e44));
    const bendSlope=sharedSlope+ridge.meanderM*(.75*fieldSlope(u/1100,ridge.seed)/1100+.25*fieldSlope(u/2300,ridge.seed^0x42454e44)/2300);
    const x=v-ridge.offsetM-bend;
    const rise=ridge.heightM*(.78+.22*field(u/880,ridge.seed^0x48454947))*taper;
    // Stretch the lee with crest curvature so a turn does not make the actual
    // normal slope much steeper than the approximately 30 degree sand face.
    const lee=rise/.55*Math.sqrt(1+bendSlope*bendSlope);
    if(x<=-windward||x>=lee)continue;
    const h=rise*ramp(x<0?1+x/windward:1-x/lee);
    if(h>height)height=h;
  }
  return height;
}
/** Most interdune flats are still sand. One broad seeded low-relief corridor
 * exposes old rock; never paint that corridor through a crest or slip face. */
export function duneSandCover(f:DuneDef,u:number,v:number,relief:number):number{
  const center=f.corridorM+commonBend(f,u)+f.halfWidthM*.035*field(u/1600,f.seed^0x434f5252);
  const exposed=(1-smooth((Math.abs(v-center)-10)/28))*(1-smooth(relief/7));
  return(.80+.20*smooth(relief/5))*(1-exposed);
}

export class DuneTerrain {
  readonly enabled:boolean;
  readonly features:readonly DuneDef[];
  private readonly cells=new Map<number,readonly DuneDef[]>();
  private readonly cellM:number;
  private readonly gridSize:number;
  private readonly seaM:number;
  constructor(private readonly def:PlanetDef,native:(x:number,y:number,z:number,out:DuneNativeSample)=>DuneNativeSample){
    this.enabled=def.terrain.amplitudeRel>=.001&&def.atmosphere!==null&&
      (def.archetype==='desert'||((def.archetype==='terran'||def.archetype==='ocean')&&def.hasFlora));
    this.cellM=Math.min(1400,def.radius*.1);this.gridSize=Math.ceil(2*def.radius/this.cellM)+3;
    this.seaM=def.seaRadius===null?-1e6:def.seaRadius-def.radius;
    const features:DuneDef[]=[];this.features=features;
    if(!this.enabled){Object.freeze(features);return;}
    const phase=rngFromString(`${def.seed}:dune-provinces:v1`)()*pmath.TWO_PI;
    const windSeed=rngFromString(`${def.seed}:dune-wind:v1`),wind=norm(windSeed()-.5,windSeed()-.5,windSeed()-.5);
    const scratch:DuneNativeSample={heightM:0,temp:0,moist:0,mountain:0,plateau:0};
    const count=768,maxFeatures=def.archetype==='desert'?42:24;
    const first=(hashSeed(`${def.seed}:dune-order`)>>>0)%count;
    for(let n=0;n<count&&features.length<maxFeatures;n++){
      const i=(first+n*347)%count;
      const y=1-2*(i+.5)/count,a=phase+i*2.399963229728653,r=Math.sqrt(1-y*y),center:V3=[r*pmath.cos(a),y,r*pmath.sin(a)];
      const s=native(...center,scratch);
      if(duneClimate(s.temp,s.moist)<.22||s.heightM<this.seaM+24||s.mountain>.18||s.plateau>.04)continue;
      const random=rngFromString(`${def.seed}:dune-province:v1:${i}`);
      const halfLengthM=Math.min(2500,def.radius*(.10+random()*.05));
      const halfWidthM=Math.min(1450,def.radius*(.055+random()*.025));
      if(features.some(f=>{const spacing=(halfWidthM+f.halfWidthM)*1.08/def.radius;return dot(center,f.center)>1-spacing*spacing*.5;}))continue;
      const windDot=dot(center,wind),projection:V3=[wind[0]-center[0]*windDot,wind[1]-center[1]*windDot,wind[2]-center[2]*windDot];
      const mag=Math.sqrt(dot(projection,projection));if(mag<.15)continue;
      const across=norm(...projection),along=cross(across,center);
      const draft={id:`${def.id}:dunes:${i}`,seed:hashSeed(`${def.seed}:dune-profile:${i}`),center,along,across,halfLengthM,halfWidthM};
      const h=s.heightM;let steep=false;
      for(const axis of[along,across])for(const sign of[-1,1]){
        const d=norm(center[0]*def.radius+axis[0]*sign*120,center[1]*def.radius+axis[1]*sign*120,center[2]*def.radius+axis[2]*sign*120);
        native(...d,scratch);if(Math.abs(scratch.heightM-h)>22)steep=true;
      }
      if(steep)continue;
      const foundation:number[]=[];
      for(let u=0;u<5;u++)for(let v=0;v<5;v++)foundation.push(native(...duneDirection(draft,def.radius,(u/2-1)*halfLengthM,(v/2-1)*halfWidthM),scratch).heightM);
      const ridges:DuneRidge[]=[];
      // Each crest has its own spacing and relief. The last incomplete ridge
      // simply fades with the same province edge as its neighbours.
      let v=-halfWidthM*.90;let j=0;
      while(v<halfWidthM&&j<12){
        const spacingM=Math.min(440,Math.max(220,halfWidthM*.23))*(.84+random()*.34);
        const heightM=Math.min(45,spacingM*.135)*( .76+random()*.24 );
        const seed=hashSeed(`${def.seed}:dune:${i}:${j}`);
        ridges.push(Object.freeze({offsetM:v,spacingM,heightM,meanderM:spacingM*(.20+random()*.20),
          centerUM:corner(0,seed^0x43454e54)*halfLengthM*.24,halfLengthM:halfLengthM*(.62+.36*(corner(0,seed^0x4c454e47)*.5+.5)),seed}));
        v+=spacingM;j++;
      }
      // Small atmospheric moons can hold only one or two crests. Two use
      // their sole gap; a one-crest province keeps its corridor outside the
      // finite footprint instead of indexing a nonexistent neighbour.
      const gap=ridges.length>2?1+Math.floor((corner(0,draft.seed^0x47415053)*.5+.5)*Math.max(1,ridges.length-3)):0;
      const left=ridges[gap],right=ridges[gap+1];
      const corridorM=left&&right?(left.offsetM+left.heightM/.55+right.offsetM-right.spacingM*.68)*.5:halfWidthM*4;
      features.push(Object.freeze({...draft,corridorM,center:Object.freeze(center),along:Object.freeze(along),across:Object.freeze(across),foundation:Object.freeze(foundation),ridges:Object.freeze(ridges)}));
    }
    Object.freeze(features);
    const mutable=new Map<number,DuneDef[]>(),R=def.radius;
    for(const f of features){
      const reach=Math.sqrt(f.halfLengthM*f.halfLengthM+f.halfWidthM*f.halfWidthM),bound=reach+R*(1-R/Math.sqrt(R*R+reach*reach));
      const lo=f.center.map(v=>Math.max(0,Math.floor((v*R-bound+R)/this.cellM))),hi=f.center.map(v=>Math.min(this.gridSize-1,Math.floor((v*R+bound+R)/this.cellM)));
      for(let x=lo[0];x<=hi[0];x++)for(let y=lo[1];y<=hi[1];y++)for(let z=lo[2];z<=hi[2];z++){
        const key=this.key(x,y,z);let list=mutable.get(key);if(!list)mutable.set(key,list=[]);list.push(f);
      }
    }
    for(const[key,list]of mutable)this.cells.set(key,Object.freeze(list));
  }
  private key(x:number,y:number,z:number):number{return(x*this.gridSize+y)*this.gridSize+z}
  private candidates(dx:number,dy:number,dz:number):readonly DuneDef[]|undefined{
    const R=this.def.radius,c=this.cellM;return this.cells.get(this.key(Math.floor((dx*R+R)/c),Math.floor((dy*R+R)/c),Math.floor((dz*R+R)/c)));
  }
  /** Check indexed footprints before spending climate noise on collision
   * queries. Conservative cell bounds alone cover much more than actual sand. */
  hasCandidates(dx:number,dy:number,dz:number):boolean{
    if(!this.enabled)return false;const list=this.candidates(dx,dy,dz);if(!list)return false;
    for(const f of list){
      const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
      const R=this.def.radius/facing,u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*R;
      if(Math.abs(u)>=f.halfLengthM)continue;
      const v=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*R;if(Math.abs(v)<f.halfWidthM)return true;
    }
    return false;
  }
  sample(dx:number,dy:number,dz:number,heightM:number,temp:number,moist:number,mountain:number,plateau:number,out:DuneSample):DuneSample{
    out.heightM=heightM;out.sand=out.weight=0;
    const climate=duneClimate(temp,moist);
    if(!this.enabled||climate<=0||heightM<=this.seaM+10||mountain>=.28||plateau>=.12)return out;
    const list=this.candidates(dx,dy,dz);if(!list)return out;
    const eligibility=climate*smooth((heightM-this.seaM-10)/30)*(1-smooth((mountain-.10)/.18))*(1-smooth(plateau/.12));
    let sum=0,target=0,sand=0,uncovered=1;
    for(const f of list){
      const facing=dx*f.center[0]+dy*f.center[1]+dz*f.center[2];if(facing<=0)continue;
      const u=(dx*f.along[0]+dy*f.along[1]+dz*f.along[2])*this.def.radius/facing,v=(dx*f.across[0]+dy*f.across[1]+dz*f.across[2])*this.def.radius/facing;
      if(Math.abs(u)>=f.halfLengthM||Math.abs(v)>=f.halfWidthM)continue;
      const footprint=smooth((1-Math.abs(u)/f.halfLengthM)/.28)*smooth((1-Math.abs(v)/f.halfWidthM)/.26);
      const gx=(v/f.halfWidthM+1)*2,gy=(u/f.halfLengthM+1)*2,x=Math.min(3,Math.floor(gx)),y=Math.min(3,Math.floor(gy)),fx=gx-x,fy=gy-y,tx=smooth(fx),ty=smooth(fy);
      const a=f.foundation[y*5+x],b=f.foundation[y*5+x+1],c=f.foundation[(y+1)*5+x],d=f.foundation[(y+1)*5+x+1];
      const base=(a*(1-tx)+b*tx)*(1-ty)+(c*(1-tx)+d*tx)*ty;
      const acrossGrade=((b-a)*(1-ty)+(d-c)*ty)*6*fx*(1-fx)/(f.halfWidthM*.5),alongGrade=((c-a)*(1-tx)+(d-b)*tx)*6*fy*(1-fy)/(f.halfLengthM*.5);
      const grade=Math.sqrt(acrossGrade*acrossGrade+alongGrade*alongGrade);
      const substrate=(1-smooth((grade-.055)/.10))*(1-smooth((Math.abs(base-heightM)-26)/48));
      const w=footprint*eligibility*substrate;if(w<=0)continue;
      const relief=duneRelief(f,u,v);
      const desired=base+relief;
      sum+=w;target+=desired*w;sand+=duneSandCover(f,u,v,relief)*w;uncovered*=1-w;
    }
    if(sum>0){out.weight=1-uncovered;out.heightM=heightM+(target/sum-heightM)*out.weight;out.sand=sand/sum*out.weight;}
    return out;
  }
}
